import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import PerformanceBadge from './PerformanceBadge';
import SafeChart from './SafeChart';

// Fitness Assessment V2 · Phase 3 — shared progress view.
//
// Consumes `GET /api/assessments/progress/{athlete_id}` (shape defined
// in `services/assessment_progress.build_progress_payload`).
//
// Two render modes:
//   • `mode="full"`  — Admin / Staff: threshold summaries, age-band
//     snapshots, full history tooltip. Verbose + technical.
//   • `mode="simple"` — Player / Parent: badge, target line,
//     one-sentence improvement message. No raw JSON, no jargon.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LOOK = {
  excellent: { label: 'Excellent', pill: 'bg-emerald-500 text-white', dot: 'bg-emerald-500' },
  good:      { label: 'Good',      pill: 'bg-blue-500 text-white',    dot: 'bg-blue-500' },
  needs_attention: { label: 'Needs Attention', pill: 'bg-rose-500 text-white', dot: 'bg-rose-500' },
  unclassified: { label: 'Unclassified', pill: 'bg-gray-300 text-gray-800 dark:bg-gray-700 dark:text-gray-300', dot: 'bg-gray-400' },
};

const CATEGORY_STROKE = {
  Power: '#f59e0b',
  Speed: '#3b82f6',
  Agility: '#10b981',
  Endurance: '#ef4444',
  Flexibility: '#ec4899',
  'Lower Body Strength': '#8b5cf6',
  'Upper Body Strength': '#7c3aed',
};

const prettyUnit = (unit) => {
  if (!unit) return '';
  if (unit === 'meters') return 'm';
  if (unit === 'seconds') return 's';
  return unit;
};

const TestCard = ({ test, mode }) => {
  const { test_type, unit, direction, history, latest, improvement,
          category_movement, excellent_target, category } = test;

  const stroke = CATEGORY_STROKE[category] || '#6366f1';
  const chartData = history.map(h => ({
    date: h.date,
    value: h.value,
    label: h.label,
    color_key: h.color_key,
    threshold_summary: h.threshold_summary,
    age_band: h.standards_age_band,
    season: h.season_snapshot,
  }));

  const targetValue = excellent_target?.value ?? null;

  const improvementClass = improvement
    ? (improvement.positive ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-rose-600 dark:text-rose-400')
    : '';

  // Y-axis domain: pad around data + target so the reference line stays visible.
  const values = chartData.map(d => d.value).filter(v => typeof v === 'number');
  if (targetValue != null) values.push(targetValue);
  const minV = values.length ? Math.min(...values) : 0;
  const maxV = values.length ? Math.max(...values) : 1;
  const pad = Math.max((maxV - minV) * 0.15, 0.5);
  const domain = [Math.max(0, minV - pad), maxV + pad];

  return (
    <Card
      className="border-l-4 h-full"
      style={{ borderLeftColor: stroke }}
      data-testid={`fit-progress-card-${test_type}`}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex justify-between items-start gap-2">
          <span className="truncate" title={test_type}>{test_type}</span>
          {direction === 'lower_is_better' && (
            <Badge variant="outline" className="text-[10px] font-normal">
              Lower is better
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-3xl font-bold" style={{ color: stroke }}
                data-testid={`fit-latest-value-${test_type}`}>
            {latest?.value ?? '-'}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">{prettyUnit(unit)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <PerformanceBadge
            label={latest?.label || 'Unclassified'}
            colorKey={latest?.color_key || 'gray'}
            reason={mode === 'full' ? latest?.threshold_summary : undefined}
            testId={`fit-badge-${test_type}`}
          />
          {category_movement && (
            <span
              className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800"
              data-testid={`fit-category-movement-${test_type}`}
            >
              {category_movement}
            </span>
          )}
        </div>

        {improvement && (
          <p className={`text-xs font-medium mb-2 ${improvementClass}`}
             data-testid={`fit-improvement-${test_type}`}>
            {improvement.text}
          </p>
        )}

        {excellent_target && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2"
             data-testid={`fit-target-${test_type}`}>
            {excellent_target.label}
          </p>
        )}

        {chartData.length >= 1 && (
          <div className="h-36">
            <SafeChart height={144}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" fontSize={10} tick={{ fill: '#6b7280' }} />
                <YAxis
                  fontSize={10}
                  tick={{ fill: '#6b7280' }}
                  domain={domain}
                  reversed={direction === 'lower_is_better'}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: 12 }}
                  formatter={(value, _name, p) => [
                    `${value} ${prettyUnit(unit)}${p?.payload?.label ? ` · ${p.payload.label}` : ''}`,
                    test_type,
                  ]}
                  labelFormatter={(l, items) => {
                    if (mode === 'full' && items?.[0]?.payload) {
                      const pl = items[0].payload;
                      const extra = [pl.age_band && `Age band ${pl.age_band}`, pl.season, pl.threshold_summary]
                        .filter(Boolean).join(' · ');
                      return `${l}${extra ? `\n${extra}` : ''}`;
                    }
                    return l;
                  }}
                />
                {targetValue != null && (
                  <ReferenceLine
                    y={targetValue}
                    stroke="#10b981"
                    strokeDasharray="4 4"
                    label={{ value: 'Excellent', position: 'insideTopRight', fill: '#10b981', fontSize: 10 }}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={stroke}
                  strokeWidth={2}
                  // Phase 85.1 — single-assessment trial athletes must
                  // still see a visible marker. Force a larger dot so
                  // a one-point line is unambiguously rendered.
                  dot={{ fill: stroke, strokeWidth: 2, r: chartData.length === 1 ? 6 : 3 }}
                  activeDot={{ r: 7 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </SafeChart>
          </div>
        )}

        {mode === 'full' && latest?.standards_age_band && (
          <p className="text-[10px] text-gray-500 dark:text-gray-500 mt-2">
            Age band: {latest.standards_age_band}
            {latest.season_snapshot ? ` · Season ${latest.season_snapshot}` : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export const FitnessProgressView = ({ athleteId, mode = 'full' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!athleteId) { setData(null); return; }
    setLoading(true);
    setError(null);
    axios.get(`${API}/assessments/progress/${athleteId}`)
      .then(res => setData(res.data))
      .catch(err => setError(err?.response?.data?.detail || 'Failed to load progress'))
      .finally(() => setLoading(false));
  }, [athleteId]);

  if (!athleteId) return null;
  if (loading) {
    return (
      <div className="flex justify-center py-8" data-testid="fit-progress-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }
  if (error) {
    return <p className="text-rose-500 text-sm" data-testid="fit-progress-error">{error}</p>;
  }
  if (!data || !data.groups?.length) {
    return (
      <p className="text-gray-500 dark:text-gray-400 text-center py-8"
         data-testid="fit-progress-empty">
        No assessment data available yet.
      </p>
    );
  }

  const status = STATUS_LOOK[data.overall_status?.key] || STATUS_LOOK.unclassified;

  return (
    <div className="space-y-6" data-testid="fit-progress-view">
      {/* Overall status card */}
      <Card>
        <CardContent className="py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className={`h-3 w-3 rounded-full ${status.dot}`} />
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Overall status</p>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${status.pill}`}
                        data-testid="fit-overall-status">
                    {data.overall_status?.label || 'Unclassified'}
                  </span>
                  {data.latest_date && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Last tested {data.latest_date}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 md:max-w-md md:text-right"
               data-testid="fit-overall-reason">
              {data.overall_status?.reason}
            </p>
          </div>

          {mode === 'full' && (data.strongest_area || data.focus_area) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
              {data.strongest_area && (
                <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3"
                     data-testid="fit-strongest-area">
                  <p className="text-[11px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Strongest area</p>
                  <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">{data.strongest_area}</p>
                </div>
              )}
              {data.focus_area && (
                <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3"
                     data-testid="fit-focus-area">
                  <p className="text-[11px] uppercase tracking-wide text-amber-700 dark:text-amber-300">Area to focus on</p>
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">{data.focus_area}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-category tabs */}
      <Tabs defaultValue={data.groups[0]?.category}>
        <TabsList className="flex-wrap">
          {data.groups.map(g => (
            <TabsTrigger key={g.category} value={g.category}
                         data-testid={`fit-tab-${g.category}`}>
              {g.category}
            </TabsTrigger>
          ))}
        </TabsList>
        {data.groups.map(g => (
          <TabsContent key={g.category} value={g.category}>
            {/* Phase 85.1 — Bilateral Sit & Reach comparison card. */}
            {g.bilateral_comparison && (
              <Card className="mt-4 border-l-4 border-l-pink-500 bg-pink-50/40 dark:bg-pink-950/10" data-testid="bilateral-comparison-card">
                <CardContent className="py-4">
                  <p className="text-[11px] uppercase tracking-wide text-pink-700 dark:text-pink-300 font-semibold mb-2">
                    Bilateral comparison · Sit &amp; Reach
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                    <div>
                      <p className="text-[10px] text-gray-500">Left</p>
                      <p className="text-lg font-bold text-gray-800 dark:text-gray-100" data-testid="bilateral-left-value">
                        {g.bilateral_comparison.left_value} {g.bilateral_comparison.unit}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Right</p>
                      <p className="text-lg font-bold text-gray-800 dark:text-gray-100" data-testid="bilateral-right-value">
                        {g.bilateral_comparison.right_value} {g.bilateral_comparison.unit}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Difference</p>
                      <p className="text-lg font-bold text-pink-700 dark:text-pink-300" data-testid="bilateral-difference">
                        {g.bilateral_comparison.difference} {g.bilateral_comparison.unit}
                      </p>
                    </div>
                  </div>
                  {g.bilateral_comparison.more_flexible_side && (
                    <div className="text-xs text-gray-600 dark:text-gray-300 space-y-0.5 mb-2">
                      <p><span className="font-semibold">More flexible side:</span> {g.bilateral_comparison.more_flexible_side}</p>
                      <p><span className="font-semibold">Side requiring greater development:</span> {g.bilateral_comparison.less_flexible_side}</p>
                    </div>
                  )}
                  <p className="text-xs text-gray-700 dark:text-gray-200 italic" data-testid="bilateral-message">
                    {g.bilateral_comparison.message}
                  </p>
                </CardContent>
              </Card>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {g.tests.map(t => (
                <TestCard key={t.test_type} test={{ ...t, category: g.category }} mode={mode} />
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default FitnessProgressView;
